// components/ReferalTotals.jsx

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Define your API base URL
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const ReferalTotals = () => {
  // State variables
  const [referralTotals, setReferralTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1); // Current page number (1-based)
  const [pageSize, setPageSize] = useState(20); // Items per page
  const [total, setTotal] = useState(0); // Total number of records
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Function to fetch referral totals
  const fetchReferralTotals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/referralActivities/totals`, {
        params: {
          page,
          limit: pageSize,
        },
      });

      if (response.status === 200) {
        const data = response.data;
        // Map the referralTotals to flatten customerDetails
        const mappedData = data.referralTotals.map(rt => ({
          _id: rt._id,
          customerName: rt.customerDetails.name,
          customerMobile: rt.customerDetails.mobile,
          customerEmail: rt.customerDetails.email || 'N/A',
          totalAmount: rt.totalAmount,
        }));
        setReferralTotals(mappedData);
        setTotal(data.total);
      } else {
        setReferralTotals([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching referral totals:', error);
      setReferralTotals([]);
      setTotal(0);
      showSnackbar('Error fetching referral totals.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReferralTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // Function to show snackbar notifications
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Function to close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Function to handle printing the referral totals
  const handlePrint = () => {
    if (referralTotals.length === 0) {
      showSnackbar('No data to print.', 'error');
      return;
    }

    const doc = new jsPDF('landscape', 'pt', 'a4');

    doc.setFontSize(16);
    doc.text('Referral Totals Report', doc.internal.pageSize.getWidth() / 2, 40, {
      align: 'center',
    });

    doc.setFontSize(12);
    const tableColumn = ['Customer Name', 'Mobile', 'Email', 'Total Amount'];
    const tableRows = referralTotals.map((rt) => [
      rt.customerName,
      rt.customerMobile,
      rt.customerEmail,
      rt.totalAmount,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 80,
      theme: 'grid',
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      styles: { fontSize: 12 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        30,
        doc.internal.pageSize.getHeight() - 10
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 70,
        doc.internal.pageSize.getHeight() - 10
      );
    }

    doc.save('Referral_Totals_Report.pdf');
  };

  // Define columns for DataGrid
  const columns = [
    { field: 'customerName', headerName: 'Customer Name', width: 200 },
    { field: 'customerMobile', headerName: 'Mobile', width: 150 },
    { field: 'customerEmail', headerName: 'Email', width: 200 },
    { field: 'totalAmount', headerName: 'Total Amount', width: 150 },
    // Removed the updatedAt column as per requirement
  ];

  return (
    <div style={{ padding: '20px' }}>
       <Box sx={{ position: 'relative', padding: 2 }}>
              {/* Place BackButton outside the Grid */}
              <BackButton />
              <br/>  <br/>
      <Typography variant="h5" align="center" gutterBottom>
        Referral Totals
      </Typography>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            fullWidth
          >
            Print Report
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          {referralTotals.length > 0 ? (
            <DataGrid
              rows={referralTotals}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[20, 40, 60]}
              pagination
              paginationMode="server"
              rowCount={total}
              onPageChange={(newPage) => setPage(newPage + 1)} // DataGrid pages are zero-based
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1); // Reset to first page
              }}
              getRowId={(row) => row._id}
              disableSelectionOnClick
            />
          ) : (
            <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
              No referral totals found.
            </Typography>
          )}
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </div>
  );
};

export default ReferalTotals;
